import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { CheckCircle2 } from 'lucide-react';

// ─────────────────────────────────────────────
// DADOS DAS ETAPAS
// ─────────────────────────────────────────────

const SECTORS = [
  { id: 'industria', label: '🏭 Indústria' },
  { id: 'comercio', label: '🛒 Comércio' },
  { id: 'servicos', label: '💼 Serviços' },
  { id: 'agronegocio', label: '🌾 Agronegócio' },
  { id: 'tecnologia', label: '💻 Tecnologia' },
  { id: 'saude', label: '🏥 Saúde' },
  { id: 'construcao', label: '🏗️ Construção' },
  { id: 'transporte', label: '🚛 Transporte' },
  { id: 'outro', label: '📦 Outro' },
];

const COMPANY_SIZES = [
  { id: 'mei', label: 'MEI', description: 'Faturamento até R$ 81 mil/ano' },
  { id: 'me', label: 'Microempresa', description: 'Até R$ 360 mil/ano' },
  { id: 'epp', label: 'Pequena Empresa', description: 'Até R$ 4,8 milhões/ano' },
  { id: 'medio', label: 'Média Empresa', description: 'Até R$ 300 milhões/ano' },
  { id: 'grande', label: 'Grande Empresa', description: 'Acima de R$ 300 milhões/ano' },
  { id: 'autonomo', label: 'Profissional Liberal', description: 'Autônomo, sem CNPJ empresarial' },
];

const STATES = [
  'AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT',
  'PA','PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO',
];

const GOALS = [
  { id: 'comprar-maquina', label: '🔧 Comprar máquina ou equipamento' },
  { id: 'capital-giro', label: '💰 Conseguir capital de giro' },
  { id: 'reforma', label: '🏗️ Reformar ou ampliar instalações' },
  { id: 'inovacao', label: '🚀 Financiar projeto de inovação' },
  { id: 'entender-opcoes', label: '📚 Entender as opções disponíveis' },
  { id: 'renovar-frota', label: '🚛 Renovar frota de veículos' },
  { id: 'exportar', label: '🌍 Financiar exportação' },
];

// ─────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────

interface OnboardingData {
  sector: string;
  companySize: string;
  state: string;
  goals: string[];
}

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, setUser } = useAuthStore();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<OnboardingData>({
    sector: '',
    companySize: '',
    state: '',
    goals: [],
  });

  const totalSteps = 3;

  const handleNext = () => {
    if (step < totalSteps) setStep((s) => s + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep((s) => s - 1);
  };

  const toggleGoal = (id: string) => {
    setData((prev) => ({
      ...prev,
      goals: prev.goals.includes(id)
        ? prev.goals.filter((g) => g !== id)
        : [...prev.goals, id],
    }));
  };

  const handleFinish = async () => {
    setIsLoading(true);
    try {
      // Salva o perfil no backend
      const updated = await api.updateOnboardingProfile ({
        sector: data.sector,
        companySize: data.companySize,
        state: data.state,
        onboardingGoals: data.goals,
        onboardingCompleted: true,
      });

      // Atualiza o store local
      if (user) {
        setUser({ ...user, ...updated });
      }

      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to save onboarding:', error);
      // Mesmo com erro, envia para o dashboard
      navigate('/dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  // Validação por etapa
  const isStepValid = () => {
    if (step === 1) return data.sector !== '' && data.companySize !== '';
    if (step === 2) return data.state !== '';
    if (step === 3) return data.goals.length > 0;
    return false;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-lg">

        {/* Progresso */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-600">
              Etapa {step} de {totalSteps}
            </p>
            <p className="text-sm text-gray-500">
              {step === 1 ? 'Sua empresa' : step === 2 ? 'Localização' : 'Seus objetivos'}
            </p>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-blue-600 to-cyan-400 h-2 rounded-full transition-all duration-500"
              style={{ width: `${(step / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        <Card className="animate-slide-up">

          {/* ── ETAPA 1: Setor e Porte ── */}
          {step === 1 && (
            <div>
              <h2 className="text-2xl font-poppins font-bold text-gray-900 mb-1">
                Conte sobre sua empresa
              </h2>
              <p className="text-gray-600 mb-6">
                Usamos isso para personalizar as orientações de financiamento.
              </p>

              {/* Setor */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Qual é o setor da sua empresa?
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {SECTORS.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setData((prev) => ({ ...prev, sector: s.id }))}
                      className={`p-3 rounded-lg border-2 text-sm font-medium text-left transition-all ${
                        data.sector === s.id
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Porte */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Qual é o porte?
                </label>
                <div className="space-y-2">
                  {COMPANY_SIZES.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setData((prev) => ({ ...prev, companySize: s.id }))}
                      className={`w-full flex items-center justify-between p-3 rounded-lg border-2 text-left transition-all ${
                        data.companySize === s.id
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div>
                        <p className={`font-semibold text-sm ${data.companySize === s.id ? 'text-blue-700' : 'text-gray-900'}`}>
                          {s.label}
                        </p>
                        <p className="text-xs text-gray-500">{s.description}</p>
                      </div>
                      {data.companySize === s.id && (
                        <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── ETAPA 2: Estado ── */}
          {step === 2 && (
            <div>
              <h2 className="text-2xl font-poppins font-bold text-gray-900 mb-1">
                Em qual estado você opera?
              </h2>
              <p className="text-gray-600 mb-6">
                Cada estado tem bancos de fomento e programas próprios que podem te beneficiar.
              </p>

              <div className="grid grid-cols-4 gap-2">
                {STATES.map((s) => (
                  <button
                    key={s}
                    onClick={() => setData((prev) => ({ ...prev, state: s }))}
                    className={`py-3 rounded-lg border-2 text-sm font-semibold transition-all ${
                      data.state === s
                        ? 'border-blue-600 bg-blue-600 text-white'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-blue-300'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>

              {/* Preview de benefício por estado */}
              {data.state && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-semibold text-blue-900 mb-1">
                    Programas disponíveis para {data.state}:
                  </p>
                  <p className="text-sm text-blue-800">
                    {getStatePrograms(data.state)}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ── ETAPA 3: Objetivos ── */}
          {step === 3 && (
            <div>
              <h2 className="text-2xl font-poppins font-bold text-gray-900 mb-1">
                O que você quer alcançar?
              </h2>
              <p className="text-gray-600 mb-6">
                Selecione todos que se aplicam. Vamos personalizar sua experiência.
              </p>

              <div className="space-y-2">
                {GOALS.map((g) => {
                  const selected = data.goals.includes(g.id);
                  return (
                    <button
                      key={g.id}
                      onClick={() => toggleGoal(g.id)}
                      className={`w-full flex items-center gap-3 p-4 rounded-lg border-2 text-left transition-all ${
                        selected
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        selected ? 'border-blue-600 bg-blue-600' : 'border-gray-300'
                      }`}>
                        {selected && (
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <p className={`font-medium text-sm ${selected ? 'text-blue-700' : 'text-gray-700'}`}>
                        {g.label}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Botões de navegação */}
          <div className="flex gap-3 mt-8">
            {step > 1 && (
              <Button
                variant="ghost"
                size="lg"
                onClick={handleBack}
                className="flex-1"
              >
                Voltar
              </Button>
            )}

            {step < totalSteps ? (
              <Button
                variant="primary"
                size="lg"
                onClick={handleNext}
                disabled={!isStepValid()}
                className="flex-1"
              >
                Continuar
              </Button>
            ) : (
              <Button
                variant="primary"
                size="lg"
                onClick={handleFinish}
                isLoading={isLoading}
                disabled={!isStepValid()}
                className="flex-1"
              >
                Começar a usar →
              </Button>
            )}
          </div>

          {/* Pular onboarding */}
          {step === 1 && (
            <div className="text-center mt-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Pular por agora
              </button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// HELPER: programas por estado
// ─────────────────────────────────────────────

function getStatePrograms(state: string): string {
  const programs: Record<string, string> = {
    SP: 'BNDES + Desenvolve SP (linha estadual exclusiva) + FAPESP (inovação)',
    MG: 'BNDES + BDMG + FAPEMIG (inovação)',
    RS: 'BNDES + BRDE (Sul)',
    SC: 'BNDES + BRDE (Sul) + FAPESC',
    PR: 'BNDES + BRDE (Sul) + Fomento Paraná',
    RJ: 'BNDES + AgeRio + FAPERJ',
    BA: 'BNDES + Desenbahia + BNB (FNE) + FAPESB',
    CE: 'BNDES + BNB (FNE, taxas reduzidas) + ADECE',
    PE: 'BNDES + BNB (FNE) + ADEPE',
    GO: 'BNDES + Goiás Fomento',
    MT: 'BNDES + MT Fomento + BASA (se município na Amazônia Legal)',
    PA: 'BNDES + BASA (FNO, taxas reduzidas para Norte)',
    AM: 'BNDES + BASA (FNO) + Zona Franca (incentivos fiscais especiais)',
  };

  return programs[state] || `BNDES Automático e FINAME disponíveis em todo o território nacional. Verifique programas estaduais de ${state}.`;
}